import * as XLSX from "xlsx";

enum FileExportFormat {
  CSV = "CSV",
  EXCEL = "Excel",
  PDF = "PDF",
}

type ExportColumn<TRow extends Record<string, any> = Record<string, any>> = {
  // property to read from each row
  dataIndex: string;
  // header label
  title: string;
  // optional transform of the raw value for export
  formatter?: (value: any, row: TRow, rowIndex: number) => any;
  // optional flag to exclude from export
  excludeFromExport?: boolean;
};

type ExportColumnsInput<
  TRow extends Record<string, any> = Record<string, any>
> = Array<string | ExportColumn<TRow>> | undefined;

interface ExportOptions<
  TRow extends Record<string, any> = Record<string, any>
> {
  // File name without extension (e.g., "customers")
  filenameBase: string;
  // Sheet/workbook title
  title?: string;
  // File export format (CSV or Excel)
  format: FileExportFormat;
  // Column definitions. If omitted, columns will be inferred from data keys.
  columns?: ExportColumnsInput<TRow>;
  // Rows to export
  rows: readonly TRow[];
}

/**
 * Normalize columns to an array of { dataIndex, title } and filter out excluded columns.
 * If no columns are provided, infer them from the rows (first-seen union of keys).
 */
export function resolveColumns<TRow extends Record<string, any>>(
  rows: readonly TRow[],
  input?: ExportColumnsInput<TRow>
): ExportColumn<TRow>[] {
  if (input && input.length > 0) {
    return input
      .map((c) =>
        typeof c === "string"
          ? ({ dataIndex: c, title: c } as ExportColumn<TRow>)
          : c
      )
      .filter((c) => !c.excludeFromExport);
  }

  // Infer headers from union of keys
  const orderedKeys: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const k of Object.keys(row ?? {})) {
      if (!seen.has(k)) {
        seen.add(k);
        orderedKeys.push(k);
      }
    }
  }
  return orderedKeys.map((key) => ({ dataIndex: key, title: key }));
}

/**
 * Convert rows into a 2D array (AOA) using the provided columns: Take values in the order of dataIndex across each row.
 */
export function extractDataRows<TRow extends Record<string, any>>(
  rows: readonly TRow[],
  columns: ExportColumn<TRow>[]
): any[][] {
  return rows.map((row, rowIndex) =>
    columns.map((col) => {
      const raw = row?.[col.dataIndex];
      return col.formatter ? col.formatter(raw, row, rowIndex) : raw ?? "";
    })
  );
}

/**
 * Build an XLSX workbook:
 * - Construct header row via column titles
 * - Convert data rows using dataIndex order
 * - Build a worksheet from AOA
 * - Set workbook props Title
 */
function createWorkbookFromObjects<TRow extends Record<string, any>>(
  title: string,
  rows: readonly TRow[],
  columnsInput?: ExportColumnsInput<TRow>
): XLSX.WorkBook {
  const columns = resolveColumns(rows, columnsInput);
  const headerRow = columns.map((c) => c.title);
  const dataRows = extractDataRows(rows, columns);

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

  workbook.Props = { Title: title };
  // As in sc-ui Table, we allow XLSX to choose default sheet name
  XLSX.utils.book_append_sheet(workbook, worksheet);
  return workbook;
}

/**
 * Write the workbook to disk using XLSX.writeFile with the appropriate extension.
 * XLSX determines the output by the filename extension.
 */
function exportObjectsWithXlsx<TRow extends Record<string, any>>({
  filenameBase,
  title = "",
  format,
  columns,
  rows,
}: ExportOptions<TRow>): void {
  const workbook = createWorkbookFromObjects(title, rows, columns);
  const ext = format === FileExportFormat.CSV ? "csv" : "xlsx";
  XLSX.writeFile(workbook, `${filenameBase}.${ext}`);
}

/**
 * Given an array of objects, export data in CSV format.
 * @example    exportObjectsAsCsv(
			`mycsvfilename-${getUnixTime(new Date())}`,
			data,
			[
				{ dataIndex: "name", title: "Name" },
				{ dataIndex: "email", title: "Email" },
			],
			"My CSV Title"
		);
 */
export function exportObjectsAsCsv<TRow extends Record<string, any>>(
  filenameBase: string,
  rows: readonly TRow[],
  columns?: ExportColumnsInput<TRow>,
  title?: string
) {
  exportObjectsWithXlsx({
    filenameBase,
    title: title ?? "",
    format: FileExportFormat.CSV,
    columns,
    rows,
  });
}

/**
 * Given an array of objects, export data in Excel (xlsx) format.
 * @example    exportObjectsAsExcel(
			`myexcelfilename-${getUnixTime(new Date())}`,
			data,
			[
				{ dataIndex: "name", title: "Name" },
				{ dataIndex: "email", title: "Email" },
			],
			"My Excel Title"
		);
 */
export function exportObjectsAsExcel<TRow extends Record<string, any>>(
  filenameBase: string,
  rows: readonly TRow[],
  columns?: ExportColumnsInput<TRow>,
  title?: string
) {
  exportObjectsWithXlsx({
    filenameBase,
    title: title ?? "",
    format: FileExportFormat.EXCEL,
    columns,
    rows,
  });
}
